import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CatalogSyncButton } from "@/components/CatalogSyncDialog";

export const vehicleLabel = (v) => [v?.brand, v?.model, v?.year].filter((x) => String(x || "").trim()).join(" ") || "—";
const catalogLabel = (fit) => fit?.catalog_all || vehicleLabel(fit?.catalog);

// Brand / model / year check between the order and its CAT # (Advance cross-reference).
// Green when they agree; amber with a signed "approve anyway" when the CAT # is for a different vehicle.
export function VehicleFitBanner({ order, fit }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [signature, setSignature] = useState("");
  const [note, setNote] = useState("");
  const approve = useMutation({
    mutationFn: () => api.post(`/orders/${order.id}/approve-catalog`, { catalog_number: fit.cat_number, employee_signature: signature.trim(), note: note.trim() || undefined }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(`CAT # ${fit.cat_number} approved for a different vehicle — signed ${signature.trim()}`); setOpen(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not approve CAT #"),
  });
  if (!fit) return null;
  const bad = [fit.brand_ok === false && "brand", fit.model_ok === false && "model", fit.year_ok === false && "year"].filter(Boolean);
  if (fit.ok) {
    return (
      <div className="text-xs font-semibold text-emerald-700" data-testid="vehicle-fit-ok">
        ✓ Fits the vehicle · CAT # {fit.cat_number} is {catalogLabel(fit)}
      </div>
    );
  }
  if (fit.acknowledged) {
    return (
      <div className="text-xs font-semibold text-amber-800" data-testid="vehicle-fit-acknowledged">
        ⚠ Different vehicle approved with signature · CAT # {fit.cat_number} is {catalogLabel(fit)} · order is {vehicleLabel(fit.order)}
      </div>
    );
  }
  return (
    <div className="rounded-md border-2 border-amber-500 bg-amber-50 p-3 text-sm text-amber-950" data-testid="vehicle-mismatch-banner">
      <div className="font-black uppercase tracking-widest text-[11px] text-amber-700">⚠ Vehicle mismatch — {bad.join(" · ") || "check"}</div>
      <div className="mt-1">
        CAT # <b className="font-mono">{fit.cat_number}</b> is <b>{catalogLabel(fit)}</b>, but this order is <b>{vehicleLabel(fit.order)}</b>.
        Check the codes / photos with the customer, assign another CAT #, or approve it anyway with your signature.
      </div>
      <div className="mt-2 flex flex-wrap justify-end gap-2">
        <CatalogSyncButton order={order} catNumber={fit.cat_number} preselect={["vehicle"]} label="Factory approved — add this car to the catalog" testid="vehicle-add-to-catalog-btn"
                           className="border-amber-500 text-amber-900 hover:bg-amber-100" />
        <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { setSignature(""); setNote(""); setOpen(true); }} data-testid="vehicle-approve-btn">
          ✓ Approve anyway (sign)
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="vehicle-approve-dialog">
          <DialogHeader><DialogTitle>⚠ Are you sure? Different vehicle</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-700">
              CAT # <b className="font-mono">{fit.cat_number}</b> is for <b>{catalogLabel(fit)}</b>; this order is <b>{vehicleLabel(fit.order)}</b>.
              Approving keeps the Catalog # on the order and records your signature.
            </p>
            <div>
              <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest">Sign to approve</label>
              <Input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder={user?.name || "Your name"} className="mt-1" data-testid="vehicle-approve-signature" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest">Note (optional)</label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this CAT # fits anyway" className="mt-1" data-testid="vehicle-approve-note" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="vehicle-approve-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" disabled={!signature.trim() || approve.isPending} onClick={() => approve.mutate()} data-testid="vehicle-approve-confirm">
              {approve.isPending ? "Saving…" : "✓ Yes — approve with my signature"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
